"use client";

import { Compass, Stamp, Scale, FileText, Briefcase, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MaterialCardGrid() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const primaryServices = [{
    numeral: "i",
    title: "Precision Laser Marking",
    description: "High-contrast, permanent identification solutions for industrial components and critical hardware.",
    Icon: Compass
  }, {
    numeral: "ii",
    title: "Custom Phenolic Tags",
    description: "Durable, weather-resistant signage engineered for harsh environments and electrical infrastructure.",
    Icon: Stamp
  }, {
    numeral: "iii",
    title: "Arc Flash Compliance",
    description: "Safety-focused labeling systems designed to meet rigorous industry standards and regulations.",
    Icon: Scale
  }];
  const secondaryServices = [{
    numeral: "iv",
    title: "Direct Part Marking",
    description: "Advanced DPM services ensuring traceability and longevity for mission-critical manufacturing parts.",
    Icon: FileText,
    span: "lg:col-span-7"
  }, {
    numeral: "v",
    title: "Industrial Asset Management",
    description: "Comprehensive identification strategies to streamline your facility's operational efficiency and maintenance.",
    Icon: Briefcase,
    span: "lg:col-span-5"
  }];
  return <section data-section-id="3568" id="material-card-grid" className="bg-background text-foreground py-24 md:py-36">
      <div className="container mx-auto px-6 md:px-10">
        {}
        <div ref={header.ref} className={`grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="col-span-12 md:col-span-3">
            <span className="font-default text-xs uppercase tracking-[0.25em] text-muted-foreground">
              03 — Capabilities
            </span>
          </div>
          <div className="col-span-12 md:col-span-9 lg:col-span-8">
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground">
              Industrial <em className="italic text-primary">Solutions</em>
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 mb-10 md:mb-12">
          {primaryServices.map((service, i) => {
          const Icon = service.Icon;
          return <article data-index={i} key={service.title} className={`group bg-card text-card-foreground border border-border rounded-lg p-8 md:p-10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg flex flex-col ${i === 1 ? "md:mt-[60px]" : ""}`}>
                <div className="flex items-center justify-between mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} />
                  <span className="font-default italic text-base text-muted-foreground tracking-wide">
                    {service.numeral}
                  </span>
                </div>

                <h3 className="font-default text-2xl md:text-[1.75rem] leading-tight text-foreground mb-4">
                  {service.title}
                </h3>

                <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-10">
                  {service.description}
                </p>

                <div className="mt-auto">
                  <Link to="/CapabilitiesPage" className="inline-flex items-center gap-2 font-default italic text-base text-foreground group/link">
                    <span className="relative">
                      Learn more
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-foreground transition-all duration-500 group-hover/link:w-full" />
                    </span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1.5} />
                  </Link>
                </div>
              </article>;
        })}
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          {secondaryServices.map((service, i) => {
          const Icon = service.Icon;
          return <article data-index={i} key={service.title} className={`group bg-card text-card-foreground border border-border rounded-lg p-8 md:p-10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg flex flex-col ${service.span}`}>
                <div className="flex items-center justify-between mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} />
                  <span className="font-default italic text-base text-muted-foreground tracking-wide">
                    {service.numeral}
                  </span>
                </div>

                <h3 className="font-default text-2xl md:text-[1.75rem] leading-tight text-foreground mb-4">
                  {service.title}
                </h3>

                <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-10 max-w-xl">
                  {service.description}
                </p>

                <div className="mt-auto">
                  <Link to="/CapabilitiesPage" className="inline-flex items-center gap-2 font-default italic text-base text-foreground group/link">
                    <span className="relative">
                      Learn more
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-foreground transition-all duration-500 group-hover/link:w-full" />
                    </span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1.5} />
                  </Link>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}